import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { Link } from '@tanstack/react-router';
import { AuthWordmark } from './AuthWordmark.js';
import { describeAuthError } from './auth-errors.js';
import './auth.css';

const requestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});
type RequestValues = z.infer<typeof requestSchema>;

const confirmSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
});
type ConfirmValues = z.infer<typeof confirmSchema>;

type Step = 'request' | 'confirm' | 'done';

export function ForgotPasswordPage(): JSX.Element {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  return (
    <div className="auth-page">
      <div className="auth-card" role="form" aria-labelledby="forgot-title">
        <AuthWordmark />
        <h1 id="forgot-title" className="auth-title">
          Reset password
        </h1>
        <p className="auth-subtitle">
          {step === 'request' && "We'll email you a verification code."}
          {step === 'confirm' && `Enter the code we sent to ${email}.`}
          {step === 'done' && 'Password updated. You can sign in with the new password.'}
        </p>

        {submitError && (
          <div className="auth-banner-error" role="alert">
            {submitError}
          </div>
        )}

        {step === 'request' && (
          <RequestForm
            onSuccess={(value) => {
              setEmail(value);
              setSubmitError(null);
              setStep('confirm');
            }}
            onError={setSubmitError}
          />
        )}

        {step === 'confirm' && (
          <ConfirmForm
            email={email}
            onSuccess={() => {
              setSubmitError(null);
              setStep('done');
            }}
            onError={setSubmitError}
          />
        )}

        {step === 'done' && (
          <Link
            to="/login"
            className="auth-button"
            style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
          >
            Back to sign in
          </Link>
        )}

        <div className="auth-link-row">
          <Link className="auth-link" to="/login">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

function RequestForm({
  onSuccess,
  onError,
}: {
  readonly onSuccess: (email: string) => void;
  readonly onError: (msg: string | null) => void;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    onError(null);
    try {
      await resetPassword({ username: values.email });
      onSuccess(values.email);
    } catch (err) {
      onError(describeAuthError(err));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <label className="auth-field">
        <span className="auth-label">Email</span>
        <input
          type="email"
          autoComplete="email"
          autoFocus
          className="auth-input"
          aria-invalid={errors.email ? 'true' : 'false'}
          {...register('email')}
        />
        {errors.email && (
          <span className="auth-error-field" role="alert">
            {errors.email.message}
          </span>
        )}
      </label>

      <button type="submit" className="auth-button" disabled={isSubmitting}>
        {isSubmitting && <span className="auth-button-spinner" aria-hidden />}
        {isSubmitting ? 'Sending…' : 'Send code'}
      </button>
    </form>
  );
}

function ConfirmForm({
  email,
  onSuccess,
  onError,
}: {
  readonly email: string;
  readonly onSuccess: () => void;
  readonly onError: (msg: string | null) => void;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ConfirmValues>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { code: '', newPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    onError(null);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: values.code,
        newPassword: values.newPassword,
      });
      onSuccess();
    } catch (err) {
      onError(describeAuthError(err));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <label className="auth-field">
        <span className="auth-label">Verification code</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          className="auth-input"
          aria-invalid={errors.code ? 'true' : 'false'}
          {...register('code')}
        />
        {errors.code && (
          <span className="auth-error-field" role="alert">
            {errors.code.message}
          </span>
        )}
      </label>

      <label className="auth-field">
        <span className="auth-label">New password</span>
        <input
          type="password"
          autoComplete="new-password"
          className="auth-input"
          aria-invalid={errors.newPassword ? 'true' : 'false'}
          {...register('newPassword')}
        />
        {errors.newPassword && (
          <span className="auth-error-field" role="alert">
            {errors.newPassword.message}
          </span>
        )}
      </label>

      <button type="submit" className="auth-button" disabled={isSubmitting}>
        {isSubmitting && <span className="auth-button-spinner" aria-hidden />}
        {isSubmitting ? 'Updating…' : 'Update password'}
      </button>
    </form>
  );
}
